import { If, asyncFlow, flow } from '@helo/utils'
import { StoreApi } from 'zustand'

import { Role } from '../../../config/roles'
import { createRouter } from '../../../router/router'
import { staticRoutes } from '../../../router/routes'
import { StoreUtils } from '../StoreUtils'
import { AuthSlice } from '../auth/authSlice'
import { CmsSlice } from '../cms'
import { CmsSliceActions } from '../cms/cmsSlice.actions'
import { PersistenceSliceActions } from '../persistence/persistenceSlice.actions'
import { QuerySliceActions } from '../queryStore/querySlice.actions'
import { ViewSliceActions } from '../view/viewSlice.actions'
import { RouterSlice } from './routerSlice'
import { RouterSliceParts, type RunPageSetupStore } from './routerSlice.parts'

const initializeRouter =
  () =>
  <T extends RouterSlice & CmsSlice & AuthSlice>(store: T): T => {
    const { hasRole } = store.auth
    const cmsRoutes = store.cms.viewConfigs
      .map(RouterSliceParts.viewConfigToRouteConfig)
      .filter(route => hasRole(route.roles as Role[]))
    const combinedRoutes = [...staticRoutes, ...cmsRoutes]
    const router = createRouter({ routes: combinedRoutes })

    return {
      ...store,
      router: {
        ...store.router,
        router,
        routes: combinedRoutes,
      },
    }
  }

const runPageSetup = (
  storeApi: StoreApi<RunPageSetupStore>,
): (<T extends RunPageSetupStore>(store: T) => Promise<T>) =>
  asyncFlow(
    asyncFlow.inputExtends<RunPageSetupStore>(),
    RouterSliceParts.synchronizeRouteStateFromBrowser(),
    RouterSliceParts.synchronizeRouteStateFromRouter(),
    If.then(RouterSliceParts.isSameRoute(), flow.return),
    RouterSliceParts.resetBrowserDocumentTitleForNavigation(),
    RouterSliceParts.updateUniqueViewKey(),
    RouterSliceParts.setComponentStatus('initializing'),
    RouterSliceParts.setPageSetupRunId(),
    StoreUtils.updateLiveStore(storeApi),
    CmsSliceActions.checkSchemaHashOnNavigate(storeApi.getState),
    QuerySliceActions.removeAllQueries(),
    PersistenceSliceActions.triggerPersistenceEvent('navigate'), // CLEAR OLD PERSISTENCE
    PersistenceSliceActions.loadPersistedItems(), // LOAD NEW PERSISTENCE.
    RouterSliceParts.synchronizeRouteConfigFromRoutes(),
    StoreUtils.updateLiveStore(storeApi), // keep route config live before view actions run.
    ViewSliceActions.initializeView(),
    ViewSliceActions.loadView(),
    RouterSliceParts.throwIfStalePageSetup(storeApi),
    RouterSliceParts.registerQueries(storeApi),
    RouterSliceParts.setComponentStatus('ready'),
  ) as <T extends RunPageSetupStore>(store: T) => Promise<T>

export const RouterSliceActions = {
  initializeRouter,
  runPageSetup,
}
